import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, timeout: 60000 });

// Inject tenant slug header automatically from sessionStorage so existing
// endpoints (employees, skill-gap, career-paths, mentor-matches, attrition,
// succession, hr-summary) serve sector-tenant data when active.
api.interceptors.request.use((config) => {
  try {
    const raw = sessionStorage.getItem("plena_tenant");
    if (raw) {
      const t = JSON.parse(raw);
      if (t?.slug) {
        config.headers["X-Tenant-Slug"] = t.slug;
      }
    }
  } catch (_) {
    // ignore
  }
  return config;
});

export const fetchPersonas = () => api.get("/personas").then((r) => r.data.personas);
export const fetchEmployees = () => api.get("/employees").then((r) => r.data.employees);
export const fetchEmployee = (id) => api.get(`/employees/${id}`).then((r) => r.data);
export const fetchSkillGap = (id, targetRoleId) =>
  api
    .get(`/employees/${id}/skill-gap`, { params: targetRoleId ? { target_role_id: targetRoleId } : {} })
    .then((r) => r.data);
export const fetchSkillGapInsight = (id, targetRoleId) =>
  api
    .get(`/employees/${id}/skill-gap/insight`, { params: targetRoleId ? { target_role_id: targetRoleId } : {} })
    .then((r) => r.data.ai_insight);
export const fetchCareerPaths = (id) => api.get(`/employees/${id}/career-paths`).then((r) => r.data);
export const fetchSkillRadar = (id, mode = "auto") => api.get(`/employees/${id}/skill-radar`, { params: { mode } }).then((r) => r.data);
export const fetchCareerPathsInsight = (id) => api.get(`/employees/${id}/career-paths/insight`).then((r) => r.data.ai_insight);
export const fetchMentorMatches = (id) => api.get(`/employees/${id}/mentor-matches`).then((r) => r.data);
export const fetchMentorMatchesInsight = (id) => api.get(`/employees/${id}/mentor-matches/insight`).then((r) => r.data.ai_insight);
export const fetchCriticalRoles = () => api.get("/succession/critical-roles").then((r) => r.data.critical_roles);
export const fetchRoleInsight = (roleId) => api.get(`/succession/critical-roles/${roleId}/insight`).then((r) => r.data);
export const fetchAttritionRisks = () => api.get("/attrition/risks").then((r) => r.data);
export const fetchHrSummary = () => api.get("/dashboard/hr-summary").then((r) => r.data);
export const fetchRoles = () => api.get("/roles").then((r) => r.data.roles);
export const askAi = (context, question) => api.post("/ai/insight", { context, question }).then((r) => r.data.answer).catch(() => null);

// Learning path
export const fetchLearningPath = (id, targetRoleId) =>
  api
    .get(`/employees/${id}/learning-path`, { params: targetRoleId ? { target_role_id: targetRoleId } : {} })
    .then((r) => r.data);
export const fetchLearningPathInsight = (id, targetRoleId) =>
  api
    .get(`/employees/${id}/learning-path/insight`, { params: targetRoleId ? { target_role_id: targetRoleId } : {} })
    .then((r) => r.data.ai_insight);

// Mentor detail (curated match id OR mentee__mentor composite)
export const fetchMentorDetail = (matchId) =>
  api.get(`/mentor-matches/${matchId}/detail`).then((r) => r.data);
export const fetchMentorDetailInsight = (matchId) =>
  api.get(`/mentor-matches/${matchId}/detail/insight`).then((r) => r.data.ai_insight);
export const submitMentorRequest = (payload) =>
  api.post("/mentor-matches/request", payload).then((r) => r.data);

// Mentor request queue (HR)
export const fetchMentorRequests = (params = {}) =>
  api.get("/mentor-requests", { params }).then((r) => r.data);
export const updateMentorRequest = (id, payload) =>
  api.patch(`/mentor-requests/${id}`, payload).then((r) => r.data);
export const fetchHrMentorRequestsDashboard = () =>
  api.get("/dashboard/hr-mentor-requests").then((r) => r.data);

// Mentor preparation pack (AI) — for mentor side
export const fetchMentorPreparation = (requestId, refresh = false) =>
  api
    .get(`/mentor-requests/${requestId}/preparation`, { params: refresh ? { refresh: true } : {} })
    .then((r) => r.data);

// Mentee preparation pack (AI) — for mentee side after they submit a request
export const fetchMenteePreparation = (requestId, refresh = false) =>
  api
    .get(`/mentor-requests/${requestId}/mentee-preparation`, { params: refresh ? { refresh: true } : {} })
    .then((r) => r.data);

// Development suggestions (HR/Manager → Employee training/project recommendations)
export const createDevSuggestion = (payload) =>
  api.post("/development-suggestions", payload).then((r) => r.data);
export const fetchDevSuggestions = (params = {}) =>
  api.get("/development-suggestions", { params }).then((r) => r.data);
export const updateDevSuggestion = (id, payload) =>
  api.patch(`/development-suggestions/${id}`, payload).then((r) => r.data);
export const fetchDevSuggestionAlternatives = (id) =>
  api.get(`/development-suggestions/${id}/ai-alternatives`).then((r) => r.data);

// Meeting notes (post-first-meeting collab notes)
export const fetchMeetingNotes = (reqId) =>
  api.get(`/mentor-requests/${reqId}/meeting-notes`).then((r) => r.data);
export const postMeetingNote = (reqId, payload) =>
  api.post(`/mentor-requests/${reqId}/meeting-notes`, payload).then((r) => r.data);
export const fetchMeetingSummary = (reqId) =>
  api.get(`/mentor-requests/${reqId}/meeting-notes/summary`).then((r) => r.data);

// Development plan (PDF data)
export const fetchDevelopmentPlan = (id, targetRoleId) =>
  api
    .get(`/employees/${id}/development-plan`, { params: targetRoleId ? { target_role_id: targetRoleId } : {} })
    .then((r) => r.data);


// ---------- Talent Management (9 Box, HiPo, Succession detail, IDP) ----------
export const fetchNineBox = (params = {}) =>
  api.get("/hr/nine-box", { params }).then((r) => r.data);
export const fetchHipoPool = () =>
  api.get("/hr/hipo-pool").then((r) => r.data);
export const fetchSuccessionDetail = () =>
  api.get("/hr/succession-detail").then((r) => r.data);
export const fetchIDP = (empId) =>
  api.get(`/employees/${empId}/idp`).then((r) => r.data);
export const saveIDP = (empId, payload) =>
  api.post(`/employees/${empId}/idp`, payload).then((r) => r.data);
export const completeIDPAction = (empId, actionId) =>
  api.post(`/employees/${empId}/idp/actions/${actionId}/complete`).then((r) => r.data);
export const fetchNineBoxEmployee = (empId) =>
  api.get(`/hr/nine-box/employee/${empId}`).then((r) => r.data);

// ---------- Faz 2: Career Engine ----------
export const listPromotionCriteria = () => api.get("/hr/promotion-criteria").then((r) => r.data);
export const fetchPromotionCriteria = (roleId) => api.get(`/hr/promotion-criteria/${roleId}`).then((r) => r.data);
export const savePromotionCriteria = (payload) => api.post("/hr/promotion-criteria", payload).then((r) => r.data);
export const fetchPromotionReadiness = (empId, roleId) => api.get(`/employees/${empId}/promotion-readiness/${roleId}`).then((r) => r.data);
export const fetchCareerTree = (empId) => api.get(`/employees/${empId}/career-tree`).then((r) => r.data);
export const listJobPostings = (status = "open") => api.get("/job-postings", { params: { status } }).then((r) => r.data);
export const saveJobPosting = (payload) => api.post("/job-postings", payload).then((r) => r.data);
export const deleteJobPosting = (id) => api.delete(`/job-postings/${id}`).then((r) => r.data);
export const submitInternalApplication = (payload) => api.post("/internal-applications", payload).then((r) => r.data);
export const listInternalApplications = (params = {}) => api.get("/internal-applications", { params }).then((r) => r.data);
export const decideInternalApplication = (id, payload) => api.post(`/internal-applications/${id}/decide`, payload).then((r) => r.data);
export const listKudosTypes = () => api.get("/recognitions/kudos-types").then((r) => r.data);
export const sendRecognition = (payload) => api.post("/recognitions", payload).then((r) => r.data);
export const listRecognitions = (params = {}) => api.get("/recognitions", { params }).then((r) => r.data);
export const fetchRecognitionLeaderboard = () => api.get("/recognitions/leaderboard").then((r) => r.data);

// ---------- 9 Box Calibration + Snapshots ----------
export const calibrateNineBox = (payload) =>
  api.post("/hr/nine-box/calibrate", payload).then((r) => r.data);
export const resetNineBoxCalibration = (empId) =>
  api.delete(`/hr/nine-box/calibrate/${empId}`).then((r) => r.data);
export const createNineBoxSnapshot = (payload) =>
  api.post("/hr/nine-box/snapshot", payload).then((r) => r.data);
export const listNineBoxSnapshots = () =>
  api.get("/hr/nine-box/snapshots").then((r) => r.data);
export const deleteNineBoxSnapshot = (snapId) =>
  api.delete(`/hr/nine-box/snapshots/${snapId}`).then((r) => r.data);
export const compareNineBoxSnapshots = (fromId, toId) =>
  api.get("/hr/nine-box/compare", { params: { from_id: fromId, ...(toId ? { to_id: toId } : {}) } }).then((r) => r.data);

// ---------- Faz 3: Certification Catalog · Target Role · Career Timeline · Kudos Points · Rewards · Career Path Editor ----------
export const fetchCertificationCatalog = (q) => api.get("/certifications/catalog", { params: q ? { q } : {} }).then((r) => r.data);
export const fetchTargetRole = (empId) => api.get(`/employees/${empId}/target-role`).then((r) => r.data);
export const setTargetRole = (empId, payload) => api.put(`/employees/${empId}/target-role`, payload).then((r) => r.data);
export const clearTargetRole = (empId) => api.delete(`/employees/${empId}/target-role`).then((r) => r.data);
export const fetchCareerTimeline = (empId) => api.get(`/employees/${empId}/career-timeline`).then((r) => r.data);
export const fetchKudosPoints = (empId) => api.get(`/employees/${empId}/kudos-points`).then((r) => r.data);
export const fetchRewardsCatalog = () => api.get("/rewards/catalog").then((r) => r.data);
export const redeemReward = (payload) => api.post("/rewards/redeem", payload).then((r) => r.data);
export const listEmployeeRedemptions = (empId) => api.get(`/employees/${empId}/redemptions`).then((r) => r.data);
export const listCareerPathOverrides = (fromRoleId) => api.get("/hr/career-paths", { params: fromRoleId ? { from_role_id: fromRoleId } : {} }).then((r) => r.data);
export const upsertCareerPathOverride = (payload) => api.post("/hr/career-paths", payload).then((r) => r.data);
export const deleteCareerPathOverride = (id) => api.delete(`/hr/career-paths/${id}`).then((r) => r.data);


// ---------- Faz 4: YGG (Yetenek Gözden Geçirme) ----------
export const listYggCycles = () => api.get("/hr/ygg-cycles").then((r) => r.data);
export const createYggCycle = (payload) => api.post("/hr/ygg-cycles", payload).then((r) => r.data);
export const getYggCycle = (id) => api.get(`/hr/ygg-cycles/${id}`).then((r) => r.data);
export const advanceYggStage = (id, stage) => api.post(`/hr/ygg-cycles/${id}/advance-stage`, null, { params: { stage } }).then((r) => r.data);
export const getYggEvaluation = (cycleId, empId) => api.get(`/ygg/${cycleId}/employees/${empId}`).then((r) => r.data);
export const submitYggSurvey = (cycleId, empId, payload) => api.post(`/ygg/${cycleId}/employees/${empId}/survey`, payload).then((r) => r.data);
export const submitYggManagerEval = (cycleId, empId, payload) => api.post(`/ygg/${cycleId}/employees/${empId}/manager-eval`, payload).then((r) => r.data);
export const submitYggDirectorReview = (cycleId, empId, payload) => api.post(`/ygg/${cycleId}/employees/${empId}/director-review`, payload).then((r) => r.data);
export const submitYggCalibration = (cycleId, empId, payload) => api.post(`/ygg/${cycleId}/employees/${empId}/calibration`, payload).then((r) => r.data);
export const fetchEmployeeYggHistory = (empId) => api.get(`/employees/${empId}/ygg-history`).then((r) => r.data);
export const fetchYggMetadata = () => api.get("/hr/ygg-metadata").then((r) => r.data);
export const getYggDirectorQueue = (cycleId, directorId) => api.get(`/ygg/director/${cycleId}/queue`, { params: { director_id: directorId } }).then((r) => r.data);
export const bulkApproveYggDirector = (cycleId, payload) => api.post(`/ygg/director/${cycleId}/bulk-approve`, payload).then((r) => r.data);
export const getYggReport = (cycleId) => api.get(`/hr/ygg-cycles/${cycleId}/report`).then((r) => r.data);
export const downloadYggReport = (cycleId) => api.get(`/hr/ygg-cycles/${cycleId}/report/download`, { responseType: "blob" }).then((r) => r.data);

// ---------- Mentor Program ----------
export const listMentors = (params) => api.get("/mentors", { params }).then((r) => r.data);
export const getMentor = (empId) => api.get(`/mentors/${empId}`).then((r) => r.data);
export const upsertMentorProfile = (empId, payload) => api.put(`/mentors/${empId}/profile`, payload).then((r) => r.data);
export const createMentoringEngagement = (payload) => api.post("/mentoring-engagements", payload).then((r) => r.data);
export const fetchMentoringEngagements = (empId) => api.get(`/employees/${empId}/mentoring-engagements`).then((r) => r.data);
export const closeMentoringEngagement = (id, outcome) => api.post(`/mentoring-engagements/${id}/close`, null, { params: { outcome } }).then((r) => r.data);
export const fetchHrMentorDashboard = () => api.get("/hr/mentor-dashboard").then((r) => r.data);

